import { createSlice } from '@reduxjs/toolkit';
import {
  getCatalogData,
  getProductDetailsData,
  getProductHistoryData,
  getProductOrderedData,
  getProductOrderGuideData,
  getAllSKUs,
  deleteProductItem,
  deleteProductsData,
  deactivateProductData
} from '../../api/catalog';

const initialState = {
  isLoading: false,
  isLoadingAllSKUsData: false,
  error: null,
  data: {
    agoraCategories: [],
    agoraSubcategories: [],
    products: [],
    vendors: [],
    productDetails: [],
    allSKUsData: [],
    productHistory: [],
    orderHistory: [],
    productOrderGuide: {
      agoraCategories: [],
      orderGuides: [],
    },
  },
};

const slice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingAllSKUsData(state) {
      state.isLoadingAllSKUsData = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getCatalogSuccess(state, action) {
      state.isLoading = false;
      //spread this in to the state so not to overwrite the state
      state.data = { ...state.data, ...action.payload };
    },

    getAllSKUsDataSuccess(state, action) {
      state.isLoadingAllSKUsData = false;
      state.data.allSKUsData = action.payload;
    },

    getProductDetailsSuccess(state, action) {
      state.isLoading = false;
      const { agoraCategories, agoraSubcategories, vendors } = action.payload;
      state.data.productDetails = action.payload;
      // agoraCategories is true
      if (agoraCategories) {
        state.data.agoraCategories = agoraCategories;
      }
      if (agoraSubcategories) {
        state.data.agoraSubcategories = agoraSubcategories;
      }
      if (vendors) {
        state.data.vendors = vendors;
      }
    },

    getProductHistorySuccess(state, action) {
      state.isLoading = false;
      state.data.productHistory = action.payload;
    },
    getOrderHistorySuccess(state, action) {
      state.isLoading = false;
      state.data.orderHistory = action.payload;
    },

    getProductOrderGuideSuccess(state, action) {
      state.isLoading = false;
      state.data.productOrderGuide = action.payload;
    },

    removeProductItem(state, action) {
      state.isLoading = false;
      const { id, productId } = action.payload;
      state.data.allSKUsData = state.data.allSKUsData.filter(sku => sku.id !== id);
      const deleteMainProduct = !state.data.allSKUsData.some(sku => sku.productId === productId);
      if (deleteMainProduct) state.data.products = state.data.products.filter(product => product.productId !== productId);
    },

    deleteItems(state, action) {
      state.isLoading = false;
      const productIds = action.payload;
      state.data.products = state.data.products.filter((product) => !productIds.includes(product.productId));
      state.data.allSKUsData = state.data.allSKUsData.filter((sku) => !productIds.includes(sku.productId));
    },

    deactivateProduct(state, action) {
      state.isLoading = false;
      const { productId, deactivate } = action.payload;
      state.data.products = state.data.products.map((product) =>
        product.productId === productId ? { ...product, deactivated: deactivate } : product
      );
      state.data.allSKUsData = state.data.allSKUsData.map((sku) =>
        sku.productId === productId ? { ...sku, deactivated: deactivate } : sku
      );
    },

    clearBoth(state, action) {
      state.data.productHistory = [];
      state.data.orderHistory = [];
    },

    updateSingleProductItemDetails(state, action) {
      const { productId, vendorItemIds } = action.payload;
      const { products } = state.data;
      state.data.products = products.map(p => p.productId === productId ? {...p, vendorItemIds} : p);
    },

  },
});

// Reducer
export default slice.reducer;

// actions
export const { updateSingleProductItemDetails } = slice.actions;

export function getCatalog() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getCatalogData();
      dispatch(slice.actions.getCatalogSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getProductDetails(productId, flag, onSuccess) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getProductDetailsData(productId, flag);
      if (onSuccess) onSuccess(response.data);
      else dispatch(slice.actions.getProductDetailsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getProductHistory(productId, vendorItemId, vendorId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getProductHistoryData(productId, vendorItemId, vendorId);
      dispatch(slice.actions.getProductHistorySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOrderHistory(productId, vendorItemId, vendorId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getProductOrderedData(productId, vendorItemId, vendorId);
      dispatch(slice.actions.getOrderHistorySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getProductOrderGuide(productId, agoraCategoryId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getProductOrderGuideData(productId, agoraCategoryId);
      dispatch(slice.actions.getProductOrderGuideSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function clearHistory() {
  return async (dispatch) => {
    dispatch(slice.actions.clearBoth());
  };
}

export function getAllSKUsData() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingAllSKUsData());
    try {
      const response = await getAllSKUs();
      dispatch(slice.actions.getAllSKUsDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deactivateProduct(productId, deactivate, enqueueSnackbar) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await deactivateProductData(productId, deactivate);
      if (response.status === 200 && response.data === true) {
        dispatch(slice.actions.deactivateProduct({ productId, deactivate }));
        enqueueSnackbar(deactivate ? 'Product deactivated' : 'Product reactivated', { variant: 'success' });
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      enqueueSnackbar(deactivate ? 'Failed to deactivate product' : 'Failed to reactivate product', { variant: 'error' });
    }
  };
}

export function removeProductItem(id, productId, enqueueSnackbar) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await deleteProductItem(id);
      if (response.status === 200)
      {
        dispatch(slice.actions.removeProductItem({ id, productId }));
        enqueueSnackbar('Product item deleted', { variant: 'success' });
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteProducts(productIds, enqueueSnackbar) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const response = await deleteProductsData(productIds).catch(() => null);
    const deletedIds = response?.status === 200 ? response.data : [];
    if (deletedIds?.length) {
      dispatch(slice.actions.deleteItems(deletedIds));
      enqueueSnackbar(`${deletedIds.length} product${deletedIds.length === 1 ? '' : 's'} deleted`, { variant: 'success' });
    } else {
      dispatch(slice.actions.hasError('Unable to delete products'));
      enqueueSnackbar('Unable to delete products', { variant: 'error' });
    }
  };
}
